import { Injectable, NgZone } from '@angular/core';
import { io, Socket } from 'socket-io-client-v4';
import { Subject } from 'rxjs';
import { environment } from '@env/environment';

type PresenceChannel = {
  here: (callback: (users: any[]) => void) => PresenceChannel;
  joining: (callback: (user: any) => void) => PresenceChannel;
  leaving: (callback: (user: any) => void) => PresenceChannel;
};

@Injectable({
  providedIn: 'root'
})
export class SocketIoService {
  private privateSocket: Socket = null; /* For logged-in users (private/presence channels) */
  private privateSocketInitSuccess: boolean = false;
  private privateChannelAndEvent: { channelName: string, eventName: string }[] = []; /* To store connected channel and event, use to re-join channel and event when socket reconnect */

  private socketCode: string;
  private socketData: Subject<{ type: 'public' | 'private' | 'presence', channelName: string, eventName: string, data: any }> = new Subject();

  private userToken: any;

  constructor(
    private ngZone: NgZone
  ) {
    if (environment.projectCode.toLowerCase() === 'qp1') {
      this.socketCode = 'qpro1';
    } else {
      this.socketCode = environment.projectCode.toLowerCase();
    }
    let lSUT: string | null = localStorage.getItem('user_token');
    this.userToken = lSUT ? JSON.parse(lSUT) : {};
  }

  /* --------------------------------------------------------------------------------- General Part --------------------------------------------------------------------------------- */
  public getSocketData(): Subject<{ type: 'public' | 'private' | 'presence', channelName: string, eventName: string, data: any }> {
    return this.socketData;
  }

  /* --------------------------------------------------------------------------------- Private Socket Part --------------------------------------------------------------------------------- */
  getPrivateSocket(): Promise<{ joinChannel: (channelName: string) => { listenEvent: (eventName: string | string[]) => void } }> {
    const
      joinChannel = (channelName: string, isReconnect: boolean = false) => {
        /* Check if joined the channel */
        if (isReconnect || !this.privateChannelAndEvent.find(x => x.channelName == channelName)) {
          this.privateSocket.emit('join', {
            channel: channelName,
            channelType: 'private'
          });
          console.debug(`(Private) Success join channel : ${channelName}`);
        } else {
          console.debug(`(Private) Already join channel : ${channelName}, will skip to avoid multiple connection.`);
        }
        return {
          listenEvent: (eventName: string | string[]): void => {
            const listenToEvent = (name: string) => {
              if (isReconnect || !this.privateChannelAndEvent.find(x => x.channelName == channelName && x.eventName == name)) {
                /* Push channel name and event name to the array list to record, and re join when socket io disconnected and reconnect */
                this.privateChannelAndEvent.push({ channelName, eventName: name });

                this.privateSocket.on(name, (socketData: any) => {
                  const { socket, _channel, _originalChannel, ...data } = socketData;
                  this.socketData.next({
                    type: 'private',
                    channelName: _originalChannel,
                    eventName: name,
                    data: data
                  });
                });
                console.debug(`(Private) Success subscribe to event : ${name}`);
              } else {
                console.debug(`(Private) Already subscribe to event : ${name}, will skip to avoid event created multiple time.`);
              }
            }

            if (Array.isArray(eventName)) {
              eventName.forEach(name => listenToEvent(name));
            } else {
              listenToEvent(eventName);
            }
          }
        }
        // }
      };

    return new Promise<{ joinChannel: (channelName: string) => { listenEvent: (eventName: string | string[]) => void } }>(async (resolve, reject) => {
      if (this.privateSocket == null) {
        try {
          /* Initialize private socket */
          await this.initPrivateSocket();

          /* On private socket connect */
          this.privateSocket.on('connect', () => {
            /* Will trigger this only when private socket reconnect */
            if (this.privateSocketInitSuccess) {
              this.privateChannelAndEvent.forEach(item => {
                this.privateSocket.removeAllListeners(item.eventName);
                joinChannel(item.channelName, true).listenEvent(item.eventName);
              });
            }

            this.privateSocketInitSuccess = true;
            console.debug("Private socket IO initialize success.");
            resolve({ joinChannel });
          });
          this.privateSocket.on('connect_error', (err) => {
            console.debug("Private socket IO initialize failed. " + err);
            reject();
          });
        } catch (err) {
          console.error("Private socket IO initialize failed.");
          reject();
        }
      } else if (this.privateSocket.connected) {
        resolve({ joinChannel });
      } else {
        let interval = setInterval(() => {
          if (this.privateSocket.connected) {
            clearInterval(interval);
            resolve({ joinChannel });
          }
        }, 500);
      }
    });
  }

  joinPresenceChannel(channelName: string, listenOnly: boolean = true): Promise<PresenceChannel> {
    return new Promise<PresenceChannel>(async (resolve, reject) => {
      try {
        const
          channel: PresenceChannel = {
            // Laravel Echo-style chaining for presence channels
            here: (callback: (members: { id: number, name: string }[]) => void) => {
              this.privateSocket.on(channelName, (data: any) => {
                if (data.type === 'here') {
                  callback(data.users);
                }
              });
              return channel;
            },
            joining: (callback: (member: { id: number, name: string }) => void) => {
              this.privateSocket.on(channelName, (data: any) => {
                if (data.type === 'joining') {
                  callback(data.user);
                }
              });
              return channel;
            },

            leaving: (callback: (member: { id: number, name: string }) => void) => {
              this.privateSocket.on(channelName, (data: any) => {
                if (data.type === 'leaving') {
                  callback(data.user);
                }
              });
              return channel;
            }
          },
          joinChannel = () => {
            this.privateSocket.emit('join', {
              channel: channelName,
              channelType: 'presence',
              listenOnly: listenOnly
            });
            console.debug(`(Presence) Success join channel : ${channelName}`);
          };

        await this.getPrivateSocket().catch(err => {
          console.debug(`(Presence) Failed to join ${channelName} channel. ` + err);
        });

        this.privateSocket.on('connect', () => {
          joinChannel();
        });

        joinChannel();
        resolve(channel);
      } catch (err) {
        console.debug(`(Presence) Failed to join ${channelName} channel. ` + err);
      }
    });
  }

  leavePresence(channelName: string): void {
    this.privateSocket.emit('leave', channelName);
    console.debug(`(Presence) Success leave channel ${channelName}.`)
  }

  leavePrivate(channelName: string, eventName: string): void {
    if (eventName) {
      this.privateChannelAndEvent = this.privateChannelAndEvent.filter(x => x.channelName != channelName || x.eventName != eventName);
      this.privateSocket.removeAllListeners(eventName);
    }
    this.privateSocket.emit('leave', channelName);
    console.debug(`(Private) Success leave channel ${channelName}${eventName ? (' and event ' + eventName) : ''}.`)
  }

  disconnectPrivateSocket(): void {
    if (this.privateSocket != null) {
      this.ngZone.runOutsideAngular(() => {
        // let interval = setInterval(() => {
        //   if (this.privateChannelAndEvent.length == 0) {
            this.privateChannelAndEvent = [];
            this.privateSocket.disconnect();
            this.privateSocket = null;
            console.debug("Private socket IO disconnected.");
            // clearInterval(interval);
        //   }
        // }, 100);
      });
    }
  }

  private initPrivateSocket(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        const
          { access_token, plaintext_token } = this.userToken,
          headers: any = {
            'X-User-Model': 'bo',
            'X-Site-Prefix': environment.sitePrefix,
            'X-Api-Base': environment.apiBase,
            'X-Project-Code': this.socketCode,
            'access-token': access_token,
            'token-selector': plaintext_token
          };

        this.privateSocket = io(environment.microServiceSocketHost, {
          withCredentials: false,
          extraHeaders: headers
        });
        resolve();
      } catch (err) {
        reject();
      };
    });
  }
}
